<!-- Copyright 2022, Common Good Learning Tools LLC -->
<template><div class="k-page-wrapper light-blue lighten-5" style="min-height:calc(100vh - 130px)">
	<h2 class="blue--text text--darken-4" style="font-weight:900"><k-icon color="blue darken-4" class="mr-2" style="margin-top:-4px; font-size:32px!important;">fas fa-briefcase</k-icon> Pathways Forward Tool</h2>

	<div class="k-hdcp-top-intro">
		<p>The Inspire Career Navigator—developed by the Georgia Department of Education—is a student-focused tool... </p>
		<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</p>
	</div>

<!-- BUBBLES/SQUIRCLES -->
	<div class="k-hdcp-stages-outer" :class="selected_career_node?'k-hdcp-career-selected':''">
		<div v-for="(stage, key) of stages" :key="key">
			<div v-if="stage.label=='spacer'" class="k-hdcp-stage-line"></div>
			<k-hover v-else v-slot:default="{hover}">
				<div class="k-hdcp-stage" 
					:class="`k-hdcp-stage-${key} k-hdcp-stage-hover-${hover||selected_stage==key}`"
					@click="stage_clicked(key)"
				>
					<k-icon class="k-hdcp-stage-icon">{{stage.icon}}</k-icon>
					<div class="k-hdcp-stage-label">{{stage.label}}</div>

					<!-- career title -->
					<div v-if="key=='career'&&selected_career_title" class="k-hdcp-stage-value">
						{{selected_career_title}}
					</div>

					<!-- ctae pathway -->
					<div v-if="key=='ctae'&&selected_ctae_paths" class="k-hdcp-stage-value">
						{{selected_ctae_paths[0].node.cfitem.fullStatement}}
						<div v-if="selected_ctae_paths.length>1" class="mt-1" style="font-size:16px">+ {{selected_ctae_paths.length-1}} additional {{U.ps('pathway', selected_ctae_paths.length-1)}}</div>
					</div>

					<!-- tcsg pathway -->
					<div v-if="key=='tcsg'&&selected_tcsg_paths" class="k-hdcp-stage-value">
						{{selected_tcsg_paths[0].node.cfitem.fullStatement}}
						<div v-if="selected_tcsg_paths.length>1" class="mt-1" style="font-size:16px">+ {{selected_tcsg_paths.length-1}} additional {{U.ps('credential', selected_tcsg_paths.length-1)}}</div>
					</div>

					<!-- usg pathway -->
					<div v-if="key=='usg'&&selected_usg_paths" class="k-hdcp-stage-value">
						{{selected_usg_paths[0].node.cfitem.fullStatement}}
						<div v-if="selected_usg_paths.length>1" class="mt-1" style="font-size:16px">+ {{selected_usg_paths.length-1}} additional {{U.ps('program/major', selected_usg_paths.length-1, 'programs/majors')}}</div>
					</div>
				</div>
			</k-hover>
		</div>
	</div>

<!-- CAREERS SECTION -->
	<k-expand-transition>
		<div v-show="selected_stage=='career'" class="k-hdcp-stage-main k-hdcp-stage-main-career">
			<div v-if="selected_career_title">
				<div class="d-flex">
					<div class="k-hdcp-featured-path">
						<k-icon :color="`${stages.career.color} darken-3`" class="mr-2">{{stages.career.icon}}</k-icon>
						Selected Career: <span style="font-weight:900">{{selected_career_title}}</span>
					</div>
					<k-spacer/>
					<div class="text-right">
						<k-btn color="#222" dark small class="k-tight-btn" @click="selecting_new_career=!selecting_new_career">Select a Different Career</k-btn>
					</div>
				</div>
				<div v-if="selected_career_description" class="mt-3">
					<b>Career Description:</b> <span v-html="selected_career_description"></span>
				</div>
				<div v-if="selected_ctae_paths">
					<div class="text-center mt-3">
						<i><b>One or more high school Career, Technical, and Agricultural Education pathways can help get you on track for this career! <nobr>Click the button below to review these pathways.</nobr></b></i>
					</div>
					<div class="text-center mt-3">
						<k-btn large color="cyan darken-3" dark @click="stage_clicked('ctae')">
							<k-icon class="mr-2">{{stages.ctae.icon}}</k-icon>
							View associated CTAE Pathways
							<k-icon class="ml-2">fas fa-right</k-icon>
						</k-btn>
					</div>
				</div>
			</div>

			<div v-if="!selected_career_title || selecting_new_career">
				<div class="text-center" :class="selected_career_title?'mt-3':''">
					<div v-if="!selected_career_title"><b>To start navigating, search or browse to find a career to explore:</b></div>
					<div v-else><b>Browse or search to find a different career to explore:</b></div>

					<div class="mt-3">
						<k-btn-toggle dense active-class="k-toggle-btn-active-class" class="k-toggle-btn" v-model="career_browse_search" mandatory>
							<k-btn light :value="'search'" @click.stop="">    Search    </k-btn>
							<k-btn light :value="'browse'" @click.stop="">    Browse    </k-btn>
						</k-btn-toggle>
					</div>
				</div>

				<div v-if="careers_branch_node" v-show="career_browse_search=='browse'" class="k-hdcp-case-tree-outer">
					<HDCPTreeItem 
						:node="careers_branch_node" 
						:icon_mappings="case_icon_mappings"
						:show_hcs_at_start="false" 
						:show_radio_for_type="career_item_type" 
					/>
				</div>

				<div v-if="careers_branch_node" v-show="career_browse_search=='search'" class="k-hdcp-case-tree-outer">
					<div class="d-flex align-center">
						<k-text-field single-line hide-details outlined dense background-color="#fff"
							v-model="career_search_string" 
							clearable
							clear-icon="fa fa-times-circle"
							prepend-inner-icon="fa fa-search"
							prepend-decorative
							aria-label="Search careers"
							label="Search careers…"
							@keyup="career_search_keyup"
						></k-text-field>
						<k-btn color="green darken-3" dark class="ml-2" @click="execute_career_search">Find Careers <k-icon small class="ml-2">fas fa-circle-arrow-right</k-icon></k-btn>
					</div>

					<div class="text-center mt-2" style="font-size:16px;">
						<i>Enter a career title or career category (like “<a @click="execute_career_search('nursing')">nursing</a>”), something you enjoy doing (like “<a @click="execute_career_search('helping other people')">helping other people</a>”), <nobr>or something you’re good at (like “<a @click="execute_career_search('working with my hands')">working with my hands</a>”).</nobr></i>
					</div>

					<!-- Career search results -->
					<div v-if="career_search_results" class="mt-3">
						<div v-for="(item, index) of career_search_results" :key="item.node.cfitem.identifier"
							v-if="index < career_search_results_n_showing"
							class="k-hdcp-tree-item-inner k-hdcp-tree-item-inner-branch" 
							:style="career_search_list_style(index)"
							@click.stop="node_selected(item.node)"
						>
							<k-icon class="k-hdcp-tree-item-selector-icon" aria-hidden="true" focusable="false">
								{{item.node.selected ? 'fas fa-square-check' : 'far fa-square'}}
							</k-icon>

							<k-icon class="k-hdcp-tree-item-icon">{{career_icon}}</k-icon>

							<k-hover v-slot:default="{hover}">
								<div class="k-hdcp-tree-item-statement" :style="item.node.selected?'font-weight:bold':''">
									{{career_title(item.node.cfitem.fullStatement)}}
									<span v-if="item.node.cfitem.humanCodingScheme && hover" class="k-hdcp-tree-item-hcs-end">({{item.node.cfitem.humanCodingScheme}})</span>
									<span v-if="hover" class="k-hdcp-tree-item-hcs-end">[{{item.match_score}}]</span>
								</div>
							</k-hover>
						</div>

						<div v-if="career_search_results_n_showing < career_search_results.length" class="mt-3">
							<k-btn small text color="green darken-3" @click="reveal_search_results(career_search_results_page_length)">
								<k-icon small class="mr-2">fas fa-plus</k-icon> Show More Careers
							</k-btn>
						</div>
					</div>
				</div>
			</div>
		</div>
	</k-expand-transition>

<!-- CTAE SECTION -->
	<k-expand-transition>
		<div v-show="selected_stage=='ctae'" class="k-hdcp-stage-main k-hdcp-stage-main-ctae">
			<div v-if="!selected_career_node" class="text-center">
				<i>Select a Career to see associated high school Career, Technical, and Agricultural Education pathways.</i>
			</div>
			<div v-else-if="!selected_ctae_paths" class="text-center">
				<i>No high school Career, Technical, and Agricultural Education pathways are associated with your chosen career.</i>
			</div>
			<div v-else>
				<div v-if="selected_ctae_paths.length==1">
					The following high school Career, Technical, and Agricultural Education pathway, which includes the course sequence shown below, will get you on track for your chosen career. Click a course to see what you’ll learn in this pathway.
				</div>
				<div v-else>
					The following {{selected_ctae_paths.length}} high school Career, Technical, and Agricultural Education pathways, which include the course sequences shown below, will get you on track for your chosen career. Click a course to see what you’ll learn in this pathway.
				</div>

				<div v-for="(path) of selected_ctae_paths" :key="path.node.cfitem.identifier" class="k-hdcp-path-list">
					<div class="k-hdcp-featured-path">
						<k-icon :color="`${stages.ctae.color} darken-3`" class="mr-2">{{stages.ctae.icon}}</k-icon>
						CTAE Pathway: <span style="font-weight:900">{{path.node.cfitem.fullStatement}}</span>
					</div>
					<div class="mt-3">
						<HDCPTreeItem v-for="(course_node) of path.node.children" :key="course_node.identifier"
							:node="course_node" 
							:icon_mappings="case_icon_mappings"
							:show_hcs_at_start="true" 
							:show_radio_for_type="''" 
						/>
					</div>
				</div>

				<div v-if="selected_tcsg_paths">
					<div class="d-flex text-center mt-3">
						<i><b>One or more Technical College System of Georgia credentials can help keep you on track for this career! <nobr>Click the button below to review these credentials.</nobr></b></i>
					</div>
					<div class="text-center mt-3">
						<k-btn large color="blue darken-3" dark @click="stage_clicked('tcsg')">
							<k-icon class="mr-2">{{stages.tcsg.icon}}</k-icon>
							View associated TCSG Credentials
							<k-icon class="ml-2">fas fa-right</k-icon>
						</k-btn>
					</div>
				</div>
			</div>
		</div>
	</k-expand-transition>

<!-- TCSG SECTION -->
	<k-expand-transition>
		<div v-show="selected_stage=='tcsg'" class="k-hdcp-stage-main k-hdcp-stage-main-tcsg">
			<div v-if="!selected_career_node" class="text-center">
				<i>Select a Career to see associated credentials you can earn at Technical College System of Georgia campuses.</i>
			</div>
			<div v-else-if="!selected_tcsg_paths" class="text-center">
				<i>No Technical College System of Georgia credentials are associated with your chosen career.</i>
			</div>
			<div v-else>
				<div>
					The following Technical College System of Georgia {{U.ps('credential', selected_tcsg_paths.length)}} will keep you on track for your chosen career.
				</div>

				<div v-for="(path) of selected_tcsg_paths" :key="path.node.cfitem.identifier" class="k-hdcp-path-list">
					<div class="k-hdcp-featured-path">
						<k-icon :color="`${stages.tcsg.color} darken-3`" class="mr-2">{{stages.tcsg.icon}}</k-icon>
						TCSG Credential: <span style="font-weight:900">{{path.node.cfitem.fullStatement}}</span>
					</div>
					<!-- currently we don't have any children of the credentials... -->
					<!-- <div class="mt-3">
						<HDCPTreeItem v-for="(course_node) of path.node.children" :key="course_node.identifier"
							:node="course_node" 
							:icon_mappings="case_icon_mappings"
							:show_hcs_at_start="true" 
							:show_radio_for_type="''" 
						/>
					</div> -->
				</div>

				<div v-if="selected_usg_paths" class="text-center mt-3">
					<k-btn large color="blue darken-3" dark @click="stage_clicked('tcsg')">View associated USG Programs and Majors<k-icon class="ml-2">fas fa-right</k-icon></k-btn>
				</div>
			</div>
		</div>
	</k-expand-transition>

<!-- USG SECTION -->
	<k-expand-transition>
		<div v-show="selected_stage=='usg'" class="k-hdcp-stage-main k-hdcp-stage-main-usg">
			<div v-if="!selected_career_node" class="text-center">
				<i>Select a Career to see associated programs and majors at University System of Georgia campuses.</i>
			</div>
			<div v-else-if="!selected_usg_paths" class="text-center">
				<i>No University System of Georgia programs or majors are associated with your chosen career.</i>
			</div>
			<div v-else>
				<div>
					The following University System of Georgia {{U.ps('program or major', selected_tcsg_paths.length, 'programs and majors')}} will get you on track for your chosen career.
				</div>

				<div v-for="(path) of selected_usg_paths" :key="path.node.cfitem.identifier" class="k-hdcp-path-list">
					<HDCPTreeItem 
						:node="path.node" 
						:icon_mappings="case_icon_mappings"
						:show_hcs_at_start="true" 
						:show_radio_for_type="''" 
					/>
				</div>
			</div>
		</div>
	</k-expand-transition>

</div></template>

<script>
import { mapState, mapGetters } from 'vuex'
import HDCPTreeItem from './HDCPTreeItem'

export default {
	components: { HDCPTreeItem },
	props: {
	},
	data() { return {
		stages: {
			career: {
				label:'Career',
				color:'green',
				icon: 'fas fa-briefcase',
			},
			spacer1: { label: 'spacer' },
			ctae: {
				label:'CTAE',
				color:'cyan',
				icon: 'fas fa-backpack',
			},
			spacer2: { label: 'spacer' },
			tcsg: {
				label:'TCSG',
				color:'blue',
				icon: 'fas fa-school',
			},
			spacer3: { label: 'spacer' },
			usg: {
				label:'USG',
				color:'purple',
				icon: 'fas fa-building-columns',
			},
		},
		selected_stage: 'career',
		// selected_stage: 'ctae',

		selected_region: 'state',

		// career_browse_search: 'browse',
		career_browse_search: 'search',
		career_search_string: '',
		selected_career_node: null,
		selecting_new_career: false,
		career_search_results: null,
		career_search_results_page_length: 15,
		career_search_results_n_showing: 0,
		career_search_results_revealing: false,

		selected_ctae_paths: null,
		selected_tcsg_paths: null,
		selected_usg_paths: null,

		// TODO: these guids should be in site_config
		soc_framework_identifier: '7d6f7a9c-671b-42a8-8b5b-8ab23396dc03',
		// careers_framework_identifier: '6a480ca2-cc6d-4a8a-9c49-81a65273f285',	// statewide only
		careers_framework_identifier: 'b8e5edd7-6dbf-49d1-a416-0934e32cd4d7',	// includes regions
		careers_branch_node_identifier: '4c803bc5-ddc5-4455-aea3-dc904fbd126d',	// this is the statewide branch
		career_item_type: 'High Demand Career',
		career_icon: 'fas fa-briefcase',

		standard_item_type: 'Element',
		standard_icon: 'fas fa-head-side-gear',

		tcsg_credential_item_type: 'Credential',
		tcsg_credential_icon: 'fas fa-certificate',

	}},
	computed: {
		...mapState(['user_info', 'frameworks_loaded', 'framework_records', 'hdcp_career_vectors']),
		...mapGetters([]),

		case_icon_mappings() {
			let o = {}
			o[this.career_item_type] = this.career_icon
			o[this.standard_item_type] = this.standard_icon
			o[this.tcsg_credential_item_type] = this.tcsg_credential_icon
			return o
		},

		soc_fr() {
			return this.framework_records.find(x=>x.lsdoc_identifier == this.soc_framework_identifier)
		},

		careers_fr() {
			return this.framework_records.find(x=>x.lsdoc_identifier == this.careers_framework_identifier)
		},

		careers_branch_node() {
			if (empty(this.careers_fr?.tree_nodes_hash)) return null
			let node = this.careers_fr.tree_nodes_hash[this.careers_branch_node_identifier]

			// make sure the node is open by default
			if (node) node.open = true
			return node
		},

		// vapp.$store.commit('lst_clear', 'hdcp_selected_career_identifier')
		hdcp_selected_career_identifier: {
			get() { return this.$store.state.lst.hdcp_selected_career_identifier },
			set(val) { this.$store.commit('lst_set', ['hdcp_selected_career_identifier', val]) }
		},

		selected_career_identifier() { return this.selected_career_node?.cfitem.identifier },
		selected_career_title() { return this.career_title(this.selected_career_node?.cfitem.fullStatement || '') },

		selected_career_description() {
			if (this.selected_career_node && this.soc_fr) {
				let cfi = this.soc_fr.json.CFItems.find(x=>x.humanCodingScheme == this.selected_career_node.cfitem.humanCodingScheme)
				if (cfi?.extensions?.supplementalNotes) {
					let s = cfi.extensions.supplementalNotes
					s = s.replace(/^Description:\s*/, '')
					s = s.replace(/\.\s*Includes .*?\./, '.')
					s = s.replace(/\.\s*Excludes .*?\./, '.')
					return s
				}
			}

			return ''
		},

		selected_career_assocs() {
			if (empty(this.careers_fr?.json?.CFAssociations)) return []
			return this.careers_fr.json.CFAssociations.filter(x=>{
				if (x.originNodeURI.identifier != this.selected_career_identifier) return false
				if (x.associationType == 'isChildOf') return false
				return true
			})
		},
	},
	watch: {
		async selected_career_node() {
			this.load_paths_for_careers()
		},
	},
	created() {
		vapp.hdcp_component = this
		this.get_initial_frameworks()
	},
	mounted() {
	},
	methods: {
		async get_initial_frameworks() {
			U.loading_start()
			// get list of all frameworks
			if (!this.frameworks_loaded) {
				try {
					await this.$store.dispatch('get_lsdoc_list')
				} catch(e) {
					console.log(e)
					this.$alert('Could not load CASE lsdoc_list.')
					return
				}
			}

			// get the soc framework
			if (!this.soc_fr?.framework_json_loaded) {
				try {
					await this.$store.dispatch('get_lsdoc', this.soc_framework_identifier)
				} catch(e) {
					console.log(e)
					this.$alert('Could not load CASE data for SOC codes.')
					return
				}
			}

			// get the careers framework
			if (!this.careers_fr?.framework_json_loaded) {
				try {
					await this.$store.dispatch('get_lsdoc', this.careers_framework_identifier)
				} catch(e) {
					console.log(e)
					this.$alert('Could not load CASE data for careers.')
					return
				}
			}

			// if we had a career in lst already, re-select it
			if (!empty(this.hdcp_selected_career_identifier)) {
				this.selected_career_node = this.careers_fr?.tree_nodes_hash[this.hdcp_selected_career_identifier]
				if (this.selected_career_node) {
					this.selected_career_node.selected = true
					// also open this item's parents
					let parent = this.selected_career_node.parent
					while (parent) {
						parent.open = true
						parent = parent.parent
					}
				}
			}
			U.loading_stop()
		},

		stage_clicked(key) {
			if (this.selected_stage == key) this.selected_stage = ''
			else this.selected_stage = key
		},

		node_selected(node) {
			// console.warn('here', node)
			// NOTE: as of now we only select nodes for the career tree
			// if the user unchecked the selected node, unselect
			if (node == this.selected_career_node) {
				this.selected_career_node = null
				this.hdcp_selected_career_identifier = ''
				node.selected = false
			
			// else the user is selecting a new node, so...
			} else {
				// unselect previous selected node if needed
				if (!empty(this.selected_career_node)) {
					this.selected_career_node.selected = false
				}
				// then select this node
				this.selected_career_node = node
				this.hdcp_selected_career_identifier = node.cfitem.identifier
				node.selected = true
				this.selecting_new_career = false
			}
		},

		career_title(fullStatement) {
			// remove the string ', All Other'
			let s = fullStatement.replace(/, All Other/, '')
			return s
		},

		career_search_keyup(evt) {
			if (evt.key == 'Enter' || evt.keyCode == 13) {
				this.execute_career_search()
			}
		},

		get_career_vectors() {
			return new Promise((resolve) => {
				let filepath = `vectors_with_notes/vector_hash_${this.careers_framework_identifier}.json`
				U.get_json_file(filepath, result=>{
					if (typeof(result) == 'string') {
						reject(result)
						return
					}

					this.$store.commit('set', ['hdcp_career_vectors', result])
					resolve()
				})
			})
		},

		get_search_vector(search_terms) {
			return new Promise((resolve) => {
				const payload = {
					user_id: this.user_info.user_id, 
					search_terms: search_terms, 
					return_search_term_vector_only: 'yes',
				}

				U.ajax('asset_search_by_keywords', payload, result=>{
					if (result.status != 'ok') {
						reject(result.status)
						return
					}
					resolve(result.vector)
				})
			})
		},

		async execute_career_search(ss) {
			// allow for a search string to be passed in
			if (typeof(ss) == 'string') {
				this.career_search_string = ss
			}

			// reset career_search_results_n_showing
			// this.career_search_results_n_showing = this.career_search_results_page_length
			this.career_search_results_n_showing = 0

			// trim/sanitize search terms; if empty, return
			let search_terms = U.sanitize_html(this.career_search_string.trim())
			if (empty(search_terms)) return

			U.loading_start()
			// load the sparkl_bot_vectors for the careers framework, if we don't already have them
			if (empty(this.hdcp_career_vectors)) {
				try {
					await this.get_career_vectors()
				} catch(e) { U.loading_stop(); console.warn(e); this.$alert('Career vectors could not be loaded.'); return }
			}

			// now get the vector for the search terms
			let search_terms_vector
			try {
				search_terms_vector = await this.get_search_vector(search_terms)
			} catch(e) { U.loading_stop(); console.warn(e); this.$alert('Search terms vector could not be loaded.'); return }

			// console.warn('worked', search_terms_vector)
			U.loading_stop()

			let arr = []

			// go through all careers in careers_branch_node
			let search_traverse = (node) => {
				if (node.cfitem?.CFItemType == this.career_item_type) {
					// found a career: get the match
					let item_vector = this.hdcp_career_vectors[node.cfitem.identifier]
					if (empty(item_vector)) {
						console.warn('couldn’t get vector for ' + node.cfitem.identifier)
						return
					}
					let cs = U.cosine_similarity(search_terms_vector, item_vector)
					let match_score = (cs > 0) ? Math.round(cs * 1000) : 0

					// push the career to arr
					arr.push({
						node: node,
						match_score: match_score,
					})
				}
				for (let child_node of node.children) {
					search_traverse(child_node)
				}
			}
			search_traverse(this.careers_branch_node)

			// sort by match_score descending
			arr.sort((a,b)=>b.match_score - a.match_score)

			// console.warn('search:', arr)
			// for (let i = 0; i < 10; ++i) console.log(`${arr[i].match_score}: ${arr[i].node.cfitem.fullStatement}`)
			this.career_search_results = arr
			this.reveal_search_results(this.career_search_results_page_length)
		},

		async reveal_search_results(n) {
			this.career_search_results_revealing = true
			this.career_search_results_n_showing += 1
			$(window).scrollTop(10000)
			for (let i = 1; i < n; ++i) {
				await new Promise(resolve => setTimeout(resolve, (i == 1 ? 200 : 100)))
				this.career_search_results_n_showing += 1
				$(window).scrollTop(10000)
			}
			this.career_search_results_revealing = false
		},

		career_search_list_style(i) {
			if (this.career_search_results_revealing) return ''

			// if all assets are showing, no fade
			if (this.career_search_results.length <= this.career_search_results_n_showing) return ''

			// if at least x items are visible, fade the final y visible items
			// if (this.visible_search_results.length < (this.items_per_page - 5)) return ''
			let from_end = this.career_search_results_n_showing - i
			if (from_end >= 5) return ''
			return `opacity:${(from_end+1)/6};`
		},

		async load_paths_for_careers() {
			// console.warn('selected_career_node watcher', this.selected_career_node)

			// start by clearing ctae, tcsg, and usg pathways
			this.selected_ctae_paths = null
			this.selected_tcsg_paths = null
			this.selected_usg_paths = null

			// if we're clearing the selected career, we're done
			if (empty(this.selected_career_node)) {
				return
			}

			U.loading_start('Loading Pathways')
			// look in assocs for pathways
			let ctae_paths = []
			let tcsg_paths = []
			let usg_paths = []

			// start by loading associated framework records
			// for (let a of this.selected_career_assocs) {
			for (let i = 0; i < this.selected_career_assocs.length; ++i) {
				let a = this.selected_career_assocs[i]
				// for satchel-created associations, we find the framework identifier like this
				let arr = a.destinationNodeURI.title.match(/.*\(:(.*):\).*/)
				if (!arr) {
					console.warn('couldn’t find framework id', object_copy(a))
					continue
				}
				let fid = arr[1]
				let item_id = a.destinationNodeURI.identifier

				// get the framework record; if not loaded, load it
				let fr = this.framework_records.find(x=>x.lsdoc_identifier == fid)
				if (!fr?.framework_json_loaded) {
					try {
						let payload = {
							lsdoc_identifier: fid,
							// include 'supplemental branches' -- i.e. aliases in the pathways folders -- in the tree structure
							include_supplemental_branches: true,
						}
						await this.$store.dispatch('get_lsdoc', payload)
					} catch(e) {
						console.log(e)
						this.$alert('Could not load CASE data for ' + fid, object_copy(a))
						continue
					}
					fr = this.framework_records.find(x=>x.lsdoc_identifier == fid)
				}
				if (!fr?.framework_json_loaded) {
					console.warn('couldn’t load framework id', object_copy(a))
					continue
				}

				// look for the tree node for the associated item
				let node = fr.tree_nodes_hash[item_id]
				if (empty(node)) {
					console.warn('couldn’t find item in framework tree', object_copy(a))
					continue
				}

				// make the node open by default
				node.open = true

				// compose data we'll save for the path
				let o = {
					fr: fr,
					node: node,
					associationType: a.associationType,
				}

				// add to ctae paths...
				if (fr.json.CFDocument.title.includes('CTAE')) {
					// console.warn('found CTAE framework!')
					ctae_paths.push(o)
				} else if (fr.json.CFDocument.title.includes('TCSG')) {
					// console.warn('found TCSG framework!')
					tcsg_paths.push(o)
				} else if (false) {	// usg??
					// console.warn('found USG framework!')
					usg_paths.push(o)
				}
			}

			// save to reactive vars
			if (ctae_paths.length > 0) this.selected_ctae_paths = ctae_paths
			if (tcsg_paths.length > 0) this.selected_tcsg_paths = tcsg_paths
			if (usg_paths.length > 0) this.selected_usg_paths = usg_paths
			U.loading_stop()
		},
	}
}
</script>

<style lang="scss">
.k-hdcp-top-intro {
	border: 8px solid #004AA3;
	border-radius: 24px;
	background-color:#f0f0f0;
	color:#000;
	padding:12px 24px;
	margin-top:24px;
}

.k-hdcp-stages-outer {
	display:flex;
	justify-content: center;
	align-items: center;
	margin:32px 0;
}

.k-hdcp-stage {
	text-align: center;
	flex: 0 0 160px;
	height: 160px;
	width: 160px;
	border: 3px solid #000;
	border-radius: 100px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	cursor: pointer;
}

.k-hdcp-stage-line {
	flex: 0 0 60px;
	width: 60px;
	border-bottom: 8px solid #000;
}

.k-hdcp-career-selected {
	.k-hdcp-stage {
		flex: 0 0 240px;
		height: 240px;
		width: 240px;
		border-radius: 120px;
	}
	.k-hdcp-stage-line {
		flex: 0 0 40px;
		width: 40px;
	}
}

.k-hdcp-stage-icon {
	margin-bottom: 4px;
	margin-top: -12px;
	font-size: 32px!important;
}

.k-hdcp-stage-label {
	font-size:24px;
	font-weight:900;
}

.k-hdcp-stage-value {
	font-size:20px;
	line-height:25px;
	margin-top:8px;
	// margin-bottom:32px;
	padding:0 8px;
}

.k-hdcp-stage-hover-true {
	border-width:8px;
}

.k-hdcp-stage-main {
	width: 1000px;
	margin:0 auto;
	border-radius:24px;
	border:8px solid;
	padding:16px;
	font-size:18px;
	line-height:24px;
	// background-color:#fff!important;

	a {
		color:#000!important;
		font-weight:bold;
		text-decoration: underline;
	}
}

// this makes the stage-main divs collapse instantly
.k-hdcp-stage-main.expand-transition-leave-active {
	transition-duration: 0ms !important;
}

.k-hdcp-stage-career, .k-hdcp-stage-main-career {
	background-color:$v-green-lighten-4;
	border-color:$v-green-darken-3;
	.k-hdcp-stage-icon { color:$v-green-darken-3 }
}
.k-hdcp-stage-main-career {
	background-color:$v-green-lighten-5;
}

.k-hdcp-stage-ctae, .k-hdcp-stage-main-ctae {
	background-color:$v-cyan-lighten-4;
	border-color:$v-cyan-darken-3;
	.k-hdcp-stage-icon { color:$v-cyan-darken-3 }
}
.k-hdcp-stage-main-ctae {
	background-color:$v-cyan-lighten-5;
}

.k-hdcp-stage-tcsg, .k-hdcp-stage-main-tcsg {
	background-color:$v-blue-lighten-4;
	border-color:$v-blue-darken-3;
	.k-hdcp-stage-icon { color:$v-blue-darken-3 }
}
.k-hdcp-stage-main-tcsg {
	background-color:$v-blue-lighten-5;
}

.k-hdcp-stage-usg, .k-hdcp-stage-main-usg {
	background-color:$v-purple-lighten-4;
	border-color:$v-purple-darken-3;
	.k-hdcp-stage-icon { color:$v-purple-darken-3 }
}
.k-hdcp-stage-main-usg {
	background-color:$v-purple-lighten-5;
}

.k-hdcp-case-tree-outer {
	margin-top:12px;
}

.k-hdcp-featured-path {
	font-size:24px;
	line-height:30px;
	.v-icon { 
		font-size:30px; 
		margin-top:-4px;
	}
}

.k-hdcp-path-list {
	border-top:1px solid #666;
	margin-top:16px;
	padding-top:8px;
}
</style>
